import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { getSites, type SitesListResponse, type SiteListItem } from '../api/sites';
import { formatRelativeTime } from '../utils/formatters';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'generating', label: 'Generating' },
  { value: 'generation-failed', label: 'Generation Failed' },
  { value: 'pending-internal-qa', label: 'Internal QA' },
  { value: 'needs-internal-revision', label: 'Needs Revision' },
  { value: 'tech-qa-failed', label: 'Tech QA Failed' },
  { value: 'pending-customer-review', label: 'Customer Review' },
  { value: 'needs-customer-revision', label: 'Customer Changes' },
  { value: 'approved-to-launch', label: 'Approved to Launch' },
  { value: 'active', label: 'Active' },
  { value: 'live', label: 'Live' },
  { value: 'suspended', label: 'Suspended' },
];

const SORTABLE_COLUMNS = [
  { value: 'created_at', label: 'Created' },
  { value: 'last_updated', label: 'Last Updated' },
  { value: 'business_name', label: 'Business Name' },
  { value: 'status', label: 'Status' },
];

const ITEMS_PER_PAGE = 25;

function SortIcon({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) {
    return (
      <svg className="w-4 h-4 text-gray-300 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-primary-600 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={direction === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
      />
    </svg>
  );
}

export function SitesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Initialize state from URL params for deep-linking support
  const [data, setData] = useState<SitesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [sortField, setSortField] = useState(searchParams.get('sort') || 'created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('order') as 'asc' | 'desc') || 'desc'
  );

  // Debounce search input
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1); // Reset to page 1 on new search
    }, 300);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // Fetch data when filters/pagination/sort change
  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getSites({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sort: sortField,
        order: sortOrder,
      });
      setData(result);
      setError(null);
    } catch (err) {
      setError('Failed to load sites');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, currentPage, sortField, sortOrder]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Sync state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (sortField !== 'created_at') params.set('sort', sortField);
    if (sortOrder !== 'desc') params.set('order', sortOrder);
    setSearchParams(params, { replace: true });
  }, [statusFilter, debouncedSearch, currentPage, sortField, sortOrder, setSearchParams]);

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSort = (column: string) => {
    if (sortField === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleRowClick = (site: SiteListItem) => {
    navigate(`/internal-qa/${site.id}`);
  };

  const totalPages = data ? Math.ceil(data.total / ITEMS_PER_PAGE) : 0;
  const totalSites = data?.total || 0;

  /**
   * Whether the custom domain is actually serving the site.
   *
   * A domain can be set on a website for days before it resolves to us — the
   * customer has to change DNS at their registrar, and that is the slowest step
   * in a launch. Until then the site is live on its pages.dev subdomain and the
   * custom domain still points wherever it did before.
   */
  const customDomainLive = (site: SiteListItem) =>
    !!site.custom_domain && site.domain_verification_status === 'connected';

  // Get domain display for a site
  const getDomainDisplay = (site: SiteListItem) => {
    if (site.custom_domain) return site.custom_domain;
    if (site.cloudflare_url) {
      try {
        return new URL(site.cloudflare_url).hostname;
      } catch {
        return site.cloudflare_url;
      }
    }
    return '—';
  };

  return (
    <PageContainer
      title="Sites"
      subtitle={loading ? 'Loading...' : `${totalSites} total sites`}
    >
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>
      )}

      {/* Status Count Pills */}
      {data?.statusCounts && Object.keys(data.statusCounts).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => handleStatusFilterChange('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({totalSites})
          </button>
          {STATUS_OPTIONS.filter(opt => opt.value !== 'all').map((opt) => {
            const count = data.statusCounts[opt.value] || 0;
            if (count === 0) return null;
            return (
              <button
                key={opt.value}
                onClick={() => handleStatusFilterChange(opt.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Search + Filter Bar */}
      <Card className="mb-6">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label htmlFor="site-search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                id="site-search"
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by business name or email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="w-56">
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select
              label="Sort by"
              options={SORTABLE_COLUMNS}
              value={sortField}
              onChange={(e) => {
                setSortField(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </Card>

      {/* Sites Table */}
      <Card padding="none">
        {loading && !data ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('business_name')}
                    >
                      <span className="flex items-center">
                        Business
                        <SortIcon active={sortField === 'business_name'} direction={sortOrder} />
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('status')}
                    >
                      <span className="flex items-center">
                        Status
                        <SortIcon active={sortField === 'status'} direction={sortOrder} />
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('created_at')}
                    >
                      <span className="flex items-center">
                        Created
                        <SortIcon active={sortField === 'created_at'} direction={sortOrder} />
                      </span>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('last_updated')}
                    >
                      <span className="flex items-center">
                        Updated
                        <SortIcon active={sortField === 'last_updated'} direction={sortOrder} />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(!data || data.sites.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        {debouncedSearch || statusFilter !== 'all'
                          ? 'No sites match the current filters'
                          : 'No sites found'}
                      </td>
                    </tr>
                  ) : (
                    data.sites.map((site) => (
                      <tr
                        key={site.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(site)}
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{site.business_name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-600">{site.customer_email}</p>
                          {site.customer_name && (
                            <p className="text-xs text-gray-400">{site.customer_name}</p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={site.status} />
                        </td>
                        <td className="px-6 py-4">
                          {/* The domain sits directly beside the status badge, so
                              the two read as a single claim. Showing a custom
                              domain unqualified next to "Live" asserted that the
                              customer's domain was up when it could still be
                              pointed at their previous host — which is exactly
                              the confusion that hid a broken domain for two days. */}
                          <p
                            className={`text-sm truncate max-w-[200px] ${
                              site.custom_domain && !customDomainLive(site)
                                ? 'text-amber-600'
                                : 'text-gray-500'
                            }`}
                          >
                            {getDomainDisplay(site)}
                          </p>
                          {site.custom_domain && !customDomainLive(site) && (
                            <p className="text-xs text-amber-600">
                              not connected · serving on subdomain
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {formatRelativeTime(site.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {site.last_updated ? formatRelativeTime(site.last_updated) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalSites)} of {totalSites}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </PageContainer>
  );
}
